import { Component, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Apollo } from 'apollo-angular';
import { ToastrService } from 'ngx-toastr';
import { MomentModule } from 'ngx-moment';
import { forkJoin } from 'rxjs';
import { cloneDeep } from 'lodash';

import {
  Admin,
  AdminAccessAuditTrail,
  AdminAccessRole,
  AdminPermissionModule,
} from '@interfaces/models.interface';
import {
  ADMIN_ACCESS_AUDIT_TRAILS,
  ADMIN_ACCESS_ROLES,
  ADMIN_PERMISSION_CATALOGUE,
  DELETE_ADMIN_ACCESS_ROLE,
  SAVE_ADMIN_ACCESS_ROLE,
} from '@gql_queries/admin/access-roles';
import { AdminPermission } from '@enums/admin-permission.enum';
import { AdminPermissionService } from '@services/admin-permission.service';
import { isUnsupportedByApi } from '@shared/utils/graphql-support.util';
import {
  isMissingPermissionError,
  MISSING_PERMISSION_MESSAGE,
} from '@shared/utils/admin-permission-error.util';

export const ROLE_NAME_MAX = 60;
export const ROLE_DESCRIPTION_MAX = 200;
const AUDIT_PAGE_SIZE = 20;

type RoleForm = {
  name: string;
  description: string;
  permissions: Set<string>;
};

type CatalogueSection = { section: string; modules: AdminPermissionModule[] };

type AuditRow = AdminAccessAuditTrail & { summary: string; targetLabel: string };

const AUDIT_ACTION_LABELS: Record<string, string> = {
  role_created: 'Created role',
  role_updated: 'Updated role',
  role_deleted: 'Deleted role',
  admin_roles_set: "Changed an admin's roles",
};

/**
 * Admin roles: list, create, edit, delete, and the activity log.
 *
 * The grid is the API's own permission catalogue, so a permission added on the
 * backend shows up here without a dashboard release. What this page enforces is
 * a courtesy — the API applies every rule again: an admin who is not a
 * superadmin can only grant or remove permissions they hold, a system role
 * cannot be renamed or deleted, and a role still assigned cannot be deleted.
 */
@Component({
  selector: 'app-admin-roles',
  standalone: true,
  imports: [CommonModule, FormsModule, MomentModule],
  templateUrl: './admin-roles.component.html',
  styleUrl: './admin-roles.component.css',
})
export class AdminRolesComponent implements OnInit {
  /** Lets the activity log name the admin a role change was made to. */
  @Input() admins: Admin[] = [];
  /** Fired after a role is saved or deleted, so the admin list can refresh. */
  @Output() rolesChanged = new EventEmitter<AdminAccessRole[]>();

  constructor(
    private apollo: Apollo,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private permissions: AdminPermissionService,
  ) {}

  public readonly nameMax = ROLE_NAME_MAX;
  public readonly descriptionMax = ROLE_DESCRIPTION_MAX;

  public catalogue: AdminPermissionModule[] = [];
  public sections: CatalogueSection[] = [];
  public roles: AdminAccessRole[] = [];
  public searchKeyword = '';

  public isLoading = false;
  public loadError: 'none' | 'unsupported' | 'failed' | 'forbidden' = 'none';

  public view: 'list' | 'role' | 'activity' = 'list';
  /** Null while creating a new role. */
  public selected: AdminAccessRole | null = null;
  public isEditing = false;
  public isSaving = false;
  public form: RoleForm = this.emptyForm();

  public auditRows: AuditRow[] = [];
  public auditPage = 1;
  public auditTotal = 0;
  public auditLoading = false;

  get canManage(): boolean {
    return this.permissions.can(AdminPermission.AdminsManage);
  }

  get permissionsIsSuperadmin(): boolean {
    return !!this.permissions.snapshot?.isSuperadmin;
  }

  get filteredRoles(): AdminAccessRole[] {
    const keyword = this.searchKeyword.trim().toLowerCase();
    if (!keyword) return this.roles;
    return this.roles.filter(
      (role) =>
        role.name.toLowerCase().includes(keyword) ||
        (role.description ?? '').toLowerCase().includes(keyword),
    );
  }

  get totalPermissions(): number {
    return this.catalogue.reduce(
      (count, module) => count + (module.view ? 1 : 0) + (module.manage ? 1 : 0),
      0,
    );
  }

  get auditPages(): number {
    return Math.max(1, Math.ceil(this.auditTotal / AUDIT_PAGE_SIZE));
  }

  ngOnInit(): void {
    this.permissions.load().subscribe();
    this.fetchAll();
  }

  private emptyForm(): RoleForm {
    return { name: '', description: '', permissions: new Set() };
  }

  fetchAll(): void {
    this.isLoading = true;
    this.loadError = 'none';

    forkJoin({
      catalogue: this.apollo.query<any>({
        query: ADMIN_PERMISSION_CATALOGUE,
        fetchPolicy: 'network-only',
      }),
      roles: this.apollo.query<any>({ query: ADMIN_ACCESS_ROLES, fetchPolicy: 'network-only' }),
    }).subscribe({
      next: ({ catalogue, roles }) => {
        this.isLoading = false;
        this.catalogue = cloneDeep(catalogue.data?.['adminPermissionCatalogue'] ?? []);
        this.sections = this.groupBySection(this.catalogue);
        this.setRoles(roles.data?.['adminAccessRoles'] ?? []);
      },
      error: (error) => {
        this.isLoading = false;
        if (isMissingPermissionError(error)) this.loadError = 'forbidden';
        else this.loadError = isUnsupportedByApi(error) ? 'unsupported' : 'failed';
      },
    });
  }

  private fetchRoles(): void {
    this.apollo
      .query<any>({ query: ADMIN_ACCESS_ROLES, fetchPolicy: 'network-only' })
      .subscribe({
        next: (res) => this.setRoles(res.data?.['adminAccessRoles'] ?? []),
        error: (error) => this.reportError(error, 'Could not refresh the roles.'),
      });
  }

  private setRoles(roles: AdminAccessRole[]): void {
    this.roles = cloneDeep(roles);
    if (this.selected) {
      this.selected = this.roles.find((role) => role.roleId === this.selected!.roleId) ?? null;
    }
    this.rolesChanged.emit(this.roles);
  }

  private groupBySection(modules: AdminPermissionModule[]): CatalogueSection[] {
    const sections: CatalogueSection[] = [];
    for (const module of modules) {
      let section = sections.find((candidate) => candidate.section === module.section);
      if (!section) {
        section = { section: module.section, modules: [] };
        sections.push(section);
      }
      section.modules.push(module);
    }
    return sections;
  }

  // ----- list -----

  openRole(role: AdminAccessRole): void {
    this.selected = role;
    this.isEditing = false;
    this.resetForm();
    this.view = 'role';
  }

  openCreate(): void {
    this.selected = null;
    this.form = this.emptyForm();
    this.isEditing = true;
    this.view = 'role';
  }

  backToList(): void {
    this.view = 'list';
    this.selected = null;
    this.isEditing = false;
    this.form = this.emptyForm();
  }

  // ----- the grid -----

  private resetForm(): void {
    const role = this.selected;
    this.form = role
      ? {
          name: role.name,
          description: role.description ?? '',
          permissions: new Set(role.permissions),
        }
      : this.emptyForm();
  }

  startEdit(): void {
    this.resetForm();
    this.isEditing = true;
  }

  cancelEdit(): void {
    if (!this.selected) {
      this.backToList();
      return;
    }
    this.resetForm();
    this.isEditing = false;
  }

  has(permission: string | null): boolean {
    return !!permission && this.form.permissions.has(permission);
  }

  /**
   * Whether the signed-in admin may switch `permission` on or off. Mirrors the
   * API's escalation rule so the grid does not offer what the save will refuse.
   */
  canToggle(permission: string | null): boolean {
    return !!permission && this.isEditing && this.permissions.can(permission);
  }

  /** Why a switch is locked, for its tooltip. */
  lockReason(permission: string | null): string {
    if (!permission || !this.isEditing) return '';
    return this.permissions.can(permission)
      ? ''
      : "You can only grant or remove permissions you hold yourself.";
  }

  /**
   * Manage implies view — someone who can change a section can see it — so
   * switching manage on switches view on, and switching view off takes manage
   * with it. The API applies the same rule on save.
   */
  toggle(module: AdminPermissionModule, action: 'view' | 'manage', checked: boolean): void {
    const permission = module[action];
    if (!permission || !this.canToggle(permission)) return;

    const next = new Set(this.form.permissions);
    if (checked) {
      next.add(permission);
      if (action === 'manage' && module.view) next.add(module.view);
    } else {
      next.delete(permission);
      if (action === 'view' && module.manage) next.delete(module.manage);
    }
    this.form = { ...this.form, permissions: next };
  }

  private validationError(): string | null {
    const name = this.form.name.trim();
    if (!name) return 'Enter a role name.';
    if (name.length > ROLE_NAME_MAX) return `Role name must not exceed ${ROLE_NAME_MAX} characters.`;
    if (this.form.description.trim().length > ROLE_DESCRIPTION_MAX) {
      return `Description must not exceed ${ROLE_DESCRIPTION_MAX} characters.`;
    }
    const duplicate = this.roles.find(
      (role) =>
        role.name.trim().toLowerCase() === name.toLowerCase() &&
        role.roleId !== this.selected?.roleId,
    );
    if (duplicate) return `A role named "${duplicate.name}" already exists.`;
    return null;
  }

  // ----- save -----

  confirmSave(modal: TemplateRef<any>): void {
    const error = this.validationError();
    if (error) {
      this.toastr.error(error);
      return;
    }
    this.modalService.open(modal, { backdrop: 'static', keyboard: false, size: 'lg' });
  }

  /** What the save changes, for the confirmation dialog. */
  get pendingChanges(): { added: string[]; removed: string[] } {
    const before = new Set(this.selected?.permissions ?? []);
    const after = this.form.permissions;
    return {
      added: [...after].filter((permission) => !before.has(permission)).map((p) => this.labelOf(p)),
      removed: [...before].filter((permission) => !after.has(permission)).map((p) => this.labelOf(p)),
    };
  }

  labelOf(permission: string): string {
    const module = this.catalogue.find(
      (candidate) => candidate.view === permission || candidate.manage === permission,
    );
    if (!module) return permission;
    return `${module.label} — ${module.manage === permission ? 'Manage' : 'View'}`;
  }

  saveRole(): void {
    const error = this.validationError();
    if (error) {
      this.toastr.error(error);
      return;
    }

    this.isSaving = true;
    const creating = !this.selected;

    this.apollo
      .mutate<any>({
        mutation: SAVE_ADMIN_ACCESS_ROLE,
        variables: {
          input: {
            ...(this.selected ? { roleId: this.selected.roleId } : {}),
            name: this.form.name.trim(),
            description: this.form.description.trim() || null,
            // Catalogue order, so the payload reads the way the grid does.
            permissions: this.orderedPermissions(this.form.permissions),
          },
        },
      })
      .subscribe({
        next: ({ data }) => {
          this.isSaving = false;
          this.modalService.dismissAll();
          const saved: AdminAccessRole | undefined = data?.['saveAdminAccessRole'];
          if (!saved) {
            this.toastr.error('The role was not saved. Please try again.');
            return;
          }

          this.selected = cloneDeep(saved);
          this.roles = creating
            ? [...this.roles, cloneDeep(saved)]
            : this.roles.map((role) => (role.roleId === saved.roleId ? cloneDeep(saved) : role));
          this.resetForm();
          this.isEditing = false;
          this.toastr.success(creating ? 'Role created.' : 'Role updated.');
          this.fetchRoles();
        },
        error: (error) => {
          this.isSaving = false;
          this.modalService.dismissAll();
          this.reportError(error, 'Error saving the role.');
        },
      });
  }

  private orderedPermissions(permissions: Set<string>): string[] {
    const ordered: string[] = [];
    for (const module of this.catalogue) {
      if (module.view && permissions.has(module.view)) ordered.push(module.view);
      if (module.manage && permissions.has(module.manage)) ordered.push(module.manage);
    }
    return ordered;
  }

  // ----- delete -----

  /** Null when the role can be deleted; otherwise why not. */
  deleteBlocker(role: AdminAccessRole | null): string | null {
    if (!role) return null;
    if (role.isSystem) return 'A system role cannot be deleted.';
    if (role.adminCount > 0) {
      return `Assigned to ${role.adminCount} admin${role.adminCount === 1 ? '' : 's'} — remove it from them first.`;
    }
    const beyond = role.permissions.filter((permission) => !this.permissions.can(permission));
    if (beyond.length > 0) {
      return 'It grants permissions you do not hold, so only someone who holds them can delete it.';
    }
    return null;
  }

  confirmDelete(modal: TemplateRef<any>): void {
    const blocker = this.deleteBlocker(this.selected);
    if (blocker) {
      this.toastr.error(blocker);
      return;
    }
    this.modalService.open(modal, { backdrop: 'static', keyboard: false });
  }

  deleteRole(): void {
    const role = this.selected;
    if (!role) return;

    this.isSaving = true;
    this.apollo
      .mutate<any>({ mutation: DELETE_ADMIN_ACCESS_ROLE, variables: { roleId: role.roleId } })
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.modalService.dismissAll();
          this.roles = this.roles.filter((candidate) => candidate.roleId !== role.roleId);
          this.rolesChanged.emit(this.roles);
          this.toastr.success(`Role "${role.name}" deleted.`);
          this.backToList();
        },
        error: (error) => {
          this.isSaving = false;
          this.modalService.dismissAll();
          this.reportError(error, 'Error deleting the role.');
        },
      });
  }

  closeAllModals(): void {
    this.modalService.dismissAll();
  }

  // ----- activity -----

  openActivity(): void {
    this.view = 'activity';
    this.fetchAudit(1);
  }

  fetchAudit(page: number): void {
    this.auditLoading = true;
    this.apollo
      .query<any>({
        query: ADMIN_ACCESS_AUDIT_TRAILS,
        variables: { page, limit: AUDIT_PAGE_SIZE },
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: (res) => {
          this.auditLoading = false;
          const result = res.data?.['adminAccessAuditTrails'];
          this.auditPage = result?.page ?? page;
          this.auditTotal = result?.total ?? 0;
          this.auditRows = (result?.items ?? []).map((row: AdminAccessAuditTrail) =>
            this.toAuditRow(row),
          );
        },
        error: (error) => {
          this.auditLoading = false;
          this.reportError(error, 'Could not load the activity log.');
        },
      });
  }

  actionLabel(action: string): string {
    return AUDIT_ACTION_LABELS[action] ?? action;
  }

  private toAuditRow(row: AdminAccessAuditTrail): AuditRow {
    let details: any = null;
    try {
      details = row.details ? JSON.parse(row.details) : null;
    } catch {
      details = null;
    }

    let targetLabel = row.targetRef;
    let summary = '';

    if (row.targetType === 'admin') {
      const admin = this.admins.find((candidate) => candidate.adminId === row.targetRef);
      targetLabel = admin ? `${admin.name} (${admin.email})` : `Admin ${row.targetRef}`;
      const before: string[] = details?.before ?? [];
      const after: string[] = details?.after ?? [];
      summary = `${before.length ? before.join(', ') : 'no roles'} → ${after.length ? after.join(', ') : 'no roles'}`;
    } else if (row.action === 'role_updated') {
      const before = new Set<string>(details?.before?.permissions ?? []);
      const after = new Set<string>(details?.after?.permissions ?? []);
      targetLabel = details?.after?.name ?? row.targetRef;
      const added = [...after].filter((permission) => !before.has(permission));
      const removed = [...before].filter((permission) => !after.has(permission));
      const parts: string[] = [];
      if (details?.before?.name && details.before.name !== details?.after?.name) {
        parts.push(`renamed from "${details.before.name}"`);
      }
      if (added.length) parts.push(`granted ${added.map((p) => this.labelOf(p)).join(', ')}`);
      if (removed.length) parts.push(`removed ${removed.map((p) => this.labelOf(p)).join(', ')}`);
      summary = parts.length ? parts.join('; ') : 'no permission changes';
    } else {
      targetLabel = details?.name ?? row.targetRef;
      const count = (details?.permissions ?? []).length;
      summary = `${count} permission${count === 1 ? '' : 's'}`;
    }

    return { ...row, summary, targetLabel };
  }

  // ----- errors -----

  private reportError(error: any, fallback: string): void {
    if (isMissingPermissionError(error)) {
      this.toastr.error(MISSING_PERMISSION_MESSAGE);
      return;
    }
    // The API's refusals (escalation, system role, still assigned) are written
    // for this screen, so show them as they are.
    this.toastr.error(error?.message || fallback);
  }
}
